import SelectComponent from "@via/components/select/select.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { AIModelType, CortexRegistryType } from "common";
import { useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

type FilterItem = { id: string; value: unknown };

type CortexFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

const ModelGardenFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
}: CortexFilterProps) => {
  const [nameFilter, setNameFilter] = useState(
    (columnFilters.find(filter => filter.id === "name")?.value as string) || ""
  );

  const [typeFilter, setTypeFilter] = useState<AIModelType | null>(
    (columnFilters.find(filter => filter.id === "type")
      ?.value as AIModelType | null) || null
  );

  const [registryFilter, setRegistryFilter] =
    useState<CortexRegistryType | null>(
      (columnFilters.find(filter => filter.id === "registry")
        ?.value as CortexRegistryType | null) || null
    );

  const debounced = useDebounceCallback((id, value) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNameFilter(value);
    debounced("name", value);
  };

  const handleTypeChange = (option: SelectOption | null) => {
    const value = option ? (option.value as AIModelType) : null;
    setTypeFilter(value);
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === "type" ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === "type") && value !== null) {
        return [...updatedFilters, { id: "type", value }];
      }
      return updatedFilters.filter(
        filter => filter.id !== "type" || filter.value !== null
      );
    });
    setPage(0);
    setPageSize(10);
  };

  const handleRegistryChange = (option: SelectOption | null) => {
    const value = option ? (option.value as CortexRegistryType) : null;
    setRegistryFilter(value);
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === "registry" ? { ...filter, value } : filter
      );
      if (
        !prevFilters.find(filter => filter.id === "registry") &&
        value !== null
      ) {
        return [...updatedFilters, { id: "registry", value }];
      }
      return updatedFilters.filter(
        filter => filter.id !== "registry" || filter.value !== null
      );
    });
    setPage(0);
    setPageSize(10);
  };

  const typeOptions = [
    { label: "Endpoint", value: AIModelType.Endpoint },
    { label: "Model", value: AIModelType.Model },
  ];

  const registryOptions = [
    { label: "Vertex AI", value: CortexRegistryType.VertexAI },
    { label: "SageMaker", value: CortexRegistryType.SageMaker },
    { label: "Tamarind", value: CortexRegistryType.Tamarind },
  ];

  return (
    <div className="w-full my-2 flex flex-col items-baseline justify-between gap-2 md:flex-row">
      <div className="w-full flex-1">
        <input
          type="text"
          placeholder="Filter By Name"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          value={nameFilter}
          onChange={handleNameChange}
        />
      </div>
      <div className="w-full flex-1">
        <SelectComponent
          name="filter-type"
          placeholder="Filter By Type"
          options={typeOptions}
          onChange={e => handleTypeChange(e as SelectOption)}
          value={
            typeFilter !== null
              ? {
                  label:
                    typeOptions.find(opt => opt.value === typeFilter)?.label ||
                    "Unknown",
                  value: typeFilter,
                }
              : null
          }
          enableClear={true}
        />
      </div>
      <div className="w-full flex-1">
        <SelectComponent
          name="filter-registry"
          placeholder="Filter By Platform"
          options={registryOptions}
          onChange={e => handleRegistryChange(e as SelectOption)}
          value={
            registryFilter !== null
              ? {
                  label:
                    registryOptions.find(opt => opt.value === registryFilter)
                      ?.label || "Unknown",
                  value: registryFilter,
                }
              : null
          }
          enableClear={true}
        />
      </div>
    </div>
  );
};

export default ModelGardenFilter;
